/**
 * 목록의 검색·필터를 주소에 둔다 — 화면·콘텐츠·플레이리스트·캠페인 목록이 같이 쓴다.
 *
 * 화면 안의 상태로 두면 상세를 열었다가 돌아오는 순간 모두 풀린다. "버전 0.7.9 만" 으로 거른 목록에서
 * 화면 하나를 고쳐 놓고 돌아오면 다시 전체 목록이라, 다음 화면을 찾으려고 같은 필터를 또 건다.
 * 주소에 실으면 뒤로 가기·새로고침·링크 공유에서 그대로 살아남는다.
 *
 * 정렬은 여기 두지 않는다 — 브라우저에 기억한다(`tableView.ts`). 사람마다 보는 순서가 있고, 링크를
 * 받은 사람의 정렬까지 바꿀 까닭이 없다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams, type NavigateOptions } from 'react-router-dom';

/** `null` 은 그 열쇠를 지운다. 목록은 쉼표로 잇는다(값은 id·코드라 쉼표가 없다). */
export type UrlPatch = Record<string, string | readonly string[] | null>;

/** 주소의 쿼리에 하나를 덧댄 결과 — 기본값과 같은 값은 넣지 않게 부르는 쪽이 `null` 을 준다. */
export function patchQuery(current: URLSearchParams, patch: UrlPatch): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const [key, value] of Object.entries(patch)) {
    if (value === null || (Array.isArray(value) && value.length === 0)) next.delete(key);
    else next.set(key, Array.isArray(value) ? value.join(',') : (value as string));
  }
  return next;
}

export function readList(params: URLSearchParams, key: string): string[] {
  return (params.get(key) ?? '').split(',').filter(Boolean);
}

export interface UrlQuery {
  get: (key: string, fallback?: string) => string;
  /** 열쇠가 있는가 — `loc=`(빈 값, "위치 미지정")과 열쇠 없음(전체)을 가른다. */
  has: (key: string) => boolean;
  list: (key: string) => string[];
  /**
   * 한 번에 여러 개를 바꾼다. **한 동작에서는 한 번만 부른다** — 같은 순간 두 번 부르면 뒤의 것이
   * 앞의 것을 덮는다(라우터가 그 그림의 주소를 기준으로 고친다).
   */
  set: (patch: UrlPatch) => void;
}

export function useUrlQuery(): UrlQuery {
  const [params, setParams] = useSearchParams();
  const set = useCallback(
    (patch: UrlPatch) => {
      // 필터를 바꾼 것은 새 자리가 아니다 — 뒤로 가기가 필터 역사를 되짚게 두지 않는다.
      setParams((prev) => patchQuery(prev, patch), { replace: true });
    },
    [setParams],
  );
  return {
    get: (key, fallback = '') => params.get(key) ?? fallback,
    has: (key) => params.has(key),
    list: (key) => readList(params, key),
    set,
  };
}

/**
 * 검색칸 — 입력은 화면 상태로 바로 그리고, 주소에는 뒤따라 적는다.
 *
 * 라우터는 주소 변경을 전환(transition)으로 미룬다. 주소 값을 그대로 칸에 물리면 그 사이 칸이 옛 값으로
 * 되돌아가 **한글 조합이 끊기고** 글자가 튄다. 우리가 적은 값이 주소에 돌아오는 것은 무시하고, 밖에서
 * 바뀐 것(필터 해제 단추, 뒤로 가기)만 칸에 들인다.
 */
export function useUrlText(url: UrlQuery, key: string): [string, (next: string) => void] {
  const value = url.get(key);
  const [text, setText] = useState(value);
  const written = useRef(new Set<string>([value]));

  useEffect(() => {
    if (written.current.has(value)) {
      if (value === text) written.current = new Set([value]);
      return;
    }
    written.current = new Set([value]);
    setText(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 주소가 바뀔 때만 본다.
  }, [value]);

  const update = useCallback(
    (next: string) => {
      written.current.add(next);
      setText(next);
      url.set({ [key]: next || null });
    },
    [url, key],
  );
  return [text, update];
}

const SCROLL_KEY = 'signageone.scroll.';

/** 콘솔의 스크롤 상자 — 문서가 아니라 오른쪽 본문(`main`)이 구른다. */
function scroller(): HTMLElement | null {
  return document.querySelector('main');
}

/**
 * 목록에서 상세로 갔다가 **뒤로 돌아오면 보던 자리로** — 상세로 가는 길(`openDetail`)과 짝이다.
 *
 * 떠나기 전의 스크롤을 이 기록 칸(`location.key`)에 적어 두고, 돌아와 목록이 다 그려지면(`ready`) 한 번
 * 되돌린다. 같은 기록 칸으로 돌아올 때만 되돌리므로, 메뉴를 눌러 새로 들어온 목록은 맨 위에서 시작한다.
 */
export function useListReturn(ready: boolean): (path: string) => void {
  const navigate = useNavigate();
  const location = useLocation();
  const restored = useRef(false);

  useEffect(() => {
    if (!ready || restored.current) return;
    restored.current = true;
    let saved: string | null = null;
    try {
      saved = window.sessionStorage.getItem(SCROLL_KEY + location.key);
      window.sessionStorage.removeItem(SCROLL_KEY + location.key);
    } catch {
      return;
    }
    const top = Number(saved);
    // 0 도 되돌린다 — 상세에서 내려가 있던 스크롤이 맨 위에 있던 목록에 그대로 남지 않게.
    if (saved !== null && Number.isFinite(top)) {
      // 그림이 자리를 잡은 다음 프레임에 — 지금은 표의 높이가 아직 다 서지 않았을 수 있다.
      requestAnimationFrame(() => scroller()?.scrollTo({ top }));
    }
  }, [ready, location.key]);

  return useCallback(
    (path: string, options: NavigateOptions = {}) => {
      try {
        window.sessionStorage.setItem(SCROLL_KEY + location.key, String(scroller()?.scrollTop ?? 0));
      } catch {
        // 기억하지 못하면 맨 위로 돌아올 뿐이다.
      }
      // 상세의 [목록으로] 가 진짜 뒤로 가기가 되게 — 온 곳을 알려 준다(주소의 필터까지).
      navigate(path, { ...options, state: { from: location.pathname + location.search } });
    },
    [navigate, location.key, location.pathname, location.search],
  );
}
